import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const CustomerGallery = () => {
  const [showLightbox, setShowLightbox] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(12);

  // Customer images - stored in public folder
  // Images will be downloaded from Google Drive and placed in /public/customers/
  const images = Array.from({ length: 32 }, (_, index) => ({
    id: index + 1,
    url: `/customers/IMG_${5950 + index}.jpg`,
    alt: `Happy Customer ${index + 1} with Perodua`
  }));

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setShowLightbox(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setShowLightbox(false);
    document.body.style.overflow = 'unset';
  };

  const goToPrevious = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const loadMore = () => {
    setVisibleCount((prev) => Math.min(prev + 12, images.length));
  };

  const visibleImages = images.slice(0, visibleCount);

  return (
    <section id="customers" className="py-20 bg-gradient-to-br from-pink-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block bg-pink-100 rounded-full px-6 py-2 mb-4">
            <span className="text-pink-700 font-semibold text-sm">❤️ CUSTOMER GALLERY</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Happy Customers 🎉
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Terima kasih atas kepercayaan anda! 32+ keluarga yang gembira dengan kereta Perodua mereka.
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
          {visibleImages.map((image, index) => (
            <Card 
              key={image.id}
              className="overflow-hidden cursor-pointer group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              onClick={() => openLightbox(index)}
            >
              <div className="relative aspect-square">
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                  <span className="text-white text-2xl">😊</span>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Load More Button */}
        {visibleCount < images.length && (
          <div className="text-center">
            <Button
              onClick={loadMore}
              className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white px-8 py-6 text-lg rounded-full shadow-lg"
            >
              📸 Load More Photos ({images.length - visibleCount} remaining)
            </Button>
          </div>
        )}

        {/* Thank You Message */}
        <div className="mt-12 text-center">
          <p className="text-slate-600 italic">
            "Real customers, real happiness. Thank you for choosing Perodua with Syasyah! 🚗❤️"
          </p>
        </div>
      </div>

      {/* Lightbox Modal */}
      {showLightbox && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-pink-400 transition-colors z-10"
            aria-label="Close"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Previous Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
            className="absolute left-4 text-white hover:text-pink-400 transition-colors z-10"
            aria-label="Previous"
          >
            <ChevronLeft className="w-12 h-12" />
          </button>

          {/* Image */}
          <div className="max-w-5xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={images[currentImageIndex].url}
              alt={images[currentImageIndex].alt}
              className="w-full h-full object-contain rounded-lg"
            />
            <p className="text-white text-center mt-4">
              {currentImageIndex + 1} / {images.length}
            </p>
          </div>

          {/* Next Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            className="absolute right-4 text-white hover:text-pink-400 transition-colors z-10"
            aria-label="Next"
          >
            <ChevronRight className="w-12 h-12" />
          </button>
        </div>
      )}
    </section>
  );
};

export default CustomerGallery;
